// components/admin/CountryVisaForm.jsx
import React, { useState, useEffect } from "react";
import { X, Upload, Globe } from "lucide-react";

const CountryVisaForm = ({ country, onClose, onSuccess }) => {
  ////!SECTION
  const [selectedFile, setSelectedFile] = useState(null);
  ////!SECTION
  const [formData, setFormData] = useState({
    name: "",
    visaCount: "",
    image: "",
    date: "",
    flag: "🇺🇸",
    description: "",
    stats: {
      countriesCovered: "150+",
      visasProcessed: "500K+",
      successRate: "98.7%",
      avgProcessingTime: "3-5 Days",
    },
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (country) {
      setFormData({
        name: country.country || "",
        visaCount: country.visaCount || "",
        image: country.image || "",
        date: country?.date?.split("T")[0] || "",
        flag: country.flag || "🇺🇸",
        description: country.description || "",
        stats: {
          countriesCovered: country?.countryCovered || "150+",
          visasProcessed: country?.visaProcess || "500K+",
          successRate: country?.successRate || "98.7%",
          avgProcessingTime: country?.averageProcessingTime || "3-5 Days",
        },
      });
    }
  }, [country]);

  const baseUrl = import.meta.env.VITE_URL || "";
  const getImageSrc = (img) =>
    !img
      ? ""
      : img.startsWith("http") || img.startsWith("data:")
        ? img
        : `${baseUrl}/${img.replace(/\\/g, "/")}`;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Implement your image upload logic here
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          image: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Country name is required";
    if (!formData.image.trim()) newErrors.image = "Image URL is required";
    if (!formData.description.trim())
      newErrors.description = "Description is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setLoading(true);

    // Create FormData
    const formDataToSend = new FormData();

    ///////////////////!SECTION
    // Append text fields
    formDataToSend.append("name", formData.name);
    formDataToSend.append("description", formData.description);
    //formDataToSend.append("date", formData.date);

    // Append stats
    formDataToSend.append("stats", JSON.stringify(formData.stats));

    //////////////////////!SECTION

    // Append image: file upload, or paste URL (create/update)
    if (selectedFile) {
      formDataToSend.append("image", selectedFile);
    } else if (formData.image && formData.image.startsWith("http")) {
      formDataToSend.append("imageUrl", formData.image);
      if (country) formDataToSend.append("existingImage", formData.image);
    } else if (
      country &&
      formData.image &&
      !formData.image.startsWith("data:")
    ) {
      formDataToSend.append("existingImage", formData.image);
    }

    setLoading(true);
    try {
      const url = country
        ? `/api/admin/visa-countries/${country._id}`
        : "/api/admin/visa-countries";

      const method = country ? "PUT" : "POST";

      const response = await fetch(`${import.meta.env.VITE_URL}${url}`, {
        method,
        credentials: "include",
        body: formDataToSend,
      });

      if (response.ok) {
        onSuccess();
      } else {
        throw new Error("Failed to save country");
      }
    } catch (error) {
      console.error("Error saving country:", error);
      alert("Failed to save country. Please try again.", error);
    } finally {
      setLoading(false);
    }
  };

  const flagOptions = [
    "🇺🇸",

    "🇬🇧",
    "🇨🇦",
    "🇦🇺",
    "🇯🇵",
    "🇰🇷",
    "🇨🇳",
    "🇮🇳",
    "🇹🇭",
    "🇻🇳",
    "🇸🇬",
    "🇲🇾",
    "🇮🇩",
    "🇵🇭",
    "🇫🇷",
    "🇩🇪",
    "🇮🇹",
    "🇪🇸",
    "🇵🇹",
    "🇳🇱",
    "🇧🇪",
    "🇨🇭",
    "🇸🇪",
    "🇳🇴",
    "🇩🇰",
    "🇫🇮",
    "🇷🇺",
    "🇧🇷",
    "🇲🇽",
    "🇦🇪",
    "🇸🇦",
    "🇶🇦",
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">
            {country ? "Edit Country" : "Add New Country"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]"
        >
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="e.g., Thailand"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.description ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Brief description about the country"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.description}
                </p>
              )}
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country Image *
              </label>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    name="image"
                    value={formData.image}
                    onChange={handleChange}
                    placeholder="Enter image URL"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.image ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.image && (
                    <p className="mt-1 text-sm text-red-600">{errors.image}</p>
                  )}
                </div>
                <div className="relative">
                  <input
                    type="file"
                    id="image-upload"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <label
                    htmlFor="image-upload"
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors"
                  >
                    <Upload size={20} />
                    Upload
                  </label>
                </div>
              </div>
              {formData.image && (
                <div className="mt-3">
                  <img
                    src={getImageSrc(formData.image)}
                    alt="Preview"
                    className="h-32 w-full object-cover rounded-lg border"
                  />
                </div>
              )}
            </div>

            {/* Stats Section */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Statistics
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Avg Processing Time
                  </label>
                  <input
                    type="text"
                    name="stats.avgProcessingTime"
                    value={formData.stats.avgProcessingTime}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            </div>

          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading
                ? "Saving..."
                : country
                ? "Update Country"
                : "Create Country"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CountryVisaForm;
